/**
 * The notification catalog the profile screen renders.
 *
 * NOTIFICATION TYPES come from the `NotificationType` union in src/shared/types.ts.
 * ONE definition, imported — this file used to hand-copy it, which meant a new type
 * (say `message_posted`) compiled clean here and then simply had no switch on the
 * screen. The catalog below is a Record over that union, so a type with no label is
 * now a compile error.
 *
 * The copy lives here because a person should read "When someone posts on the board",
 * never `message_posted`.
 *
 * AUDIENCE MATTERS. Money notifications are raised with audience 'adults', so a
 * child can never receive one — offering a child a switch for it would be a switch
 * that does nothing. Each entry says who it is actually for, and the screen only
 * shows a person the ones that can reach them.
 */
import type { NotificationType, Role } from '../../shared/types';

export type NotifyGroup = 'Chores and allowance' | 'Money' | 'Around the house' | 'Where everyone is';

export interface NotifyMeta {
  type: NotificationType;
  /** First person, plain language. This is the switch's label. */
  label: string;
  group: NotifyGroup;
  /** Who can actually receive it. 'adult' = raised with audience 'adults'. */
  audience: 'all' | 'adult';
  /** Reading order on the screen. Lower first. */
  order: number;
}

/**
 * Every type the app raises. Record<NotificationType, …> is the whole point: add a
 * type to the union in shared/types.ts and this file stops building until someone
 * writes the sentence a nine-year-old will read.
 */
const NOTIFY_META: Record<NotificationType, Omit<NotifyMeta, 'type'>> = {
  chore_done: {
    label: 'When a chore is waiting on my approval',
    group: 'Chores and allowance',
    audience: 'adult',
    order: 10,
  },
  chore_assigned: {
    label: 'When I get a new chore',
    group: 'Chores and allowance',
    audience: 'all',
    order: 20,
  },
  chore_approved: {
    label: 'When a grown-up approves my chore',
    group: 'Chores and allowance',
    audience: 'all',
    order: 30,
  },
  chore_dismissed: {
    label: 'When a chore is sent back to my list',
    group: 'Chores and allowance',
    audience: 'all',
    order: 40,
  },
  allowance_payout: {
    label: 'When allowance is paid out to me',
    group: 'Chores and allowance',
    audience: 'all',
    order: 50,
  },
  budget_near: {
    label: 'When a budget is getting close to its cap',
    group: 'Money',
    audience: 'adult',
    order: 60,
  },
  budget_over: {
    label: 'When a budget goes over its cap',
    group: 'Money',
    audience: 'adult',
    order: 70,
  },
  subscription_new: {
    label: 'When a new subscription turns up',
    group: 'Money',
    audience: 'adult',
    order: 80,
  },
  subscription_increase: {
    label: 'When a subscription raises its price',
    group: 'Money',
    audience: 'adult',
    order: 90,
  },
  message_posted: {
    label: 'When someone posts on the board',
    group: 'Around the house',
    audience: 'all',
    order: 100,
  },
  reminder_due: {
    label: 'When a reminder comes due',
    group: 'Around the house',
    audience: 'all',
    order: 101,
  },
  birthday_soon: {
    label: 'When a birthday or anniversary is coming up',
    group: 'Around the house',
    audience: 'all',
    order: 102,
  },
  goal_reached: {
    label: 'When I reach a savings goal',
    group: 'Chores and allowance',
    audience: 'all',
    order: 45,
  },
  meal_plan_empty: {
    label: 'When nobody has planned the week’s meals yet',
    group: 'Around the house',
    audience: 'all',
    order: 110,
  },
  place_arrival: {
    label: 'When someone arrives at a place',
    group: 'Where everyone is',
    audience: 'all',
    order: 120,
  },
  place_departure: {
    label: 'When someone leaves a place',
    group: 'Where everyone is',
    audience: 'all',
    order: 121,
  },
  battery_low: {
    label: 'When someone’s phone battery is low',
    group: 'Where everyone is',
    audience: 'all',
    order: 122,
  },
};

/** The catalog, in reading order. Derived from the record so it can never miss a type. */
export const NOTIFY_CATALOG: NotifyMeta[] = (Object.keys(NOTIFY_META) as NotificationType[])
  .map((type) => ({ type, ...NOTIFY_META[type] }))
  .sort((a, b) => a.order - b.order);

export const NOTIFY_GROUPS: NotifyGroup[] = [
  'Chores and allowance',
  'Money',
  'Around the house',
  'Where everyone is',
];

/** The switches a person of this role can actually be reached by. */
export function notifyCatalogFor(role: Role): NotifyMeta[] {
  return role === 'adult' ? NOTIFY_CATALOG : NOTIFY_CATALOG.filter((n) => n.audience === 'all');
}
